/**
 * Infinite Scroll for Collection Grid
 * Automatically loads more products as user scrolls to the bottom
 */
class InfiniteScroll {
  constructor() {
    this.container = document.getElementById('product-grid');
    this.gridContainer = document.getElementById('ProductGridContainer');
    this.pagination = document.querySelector('.pagination-wrapper');
    this.loading = false;
    this.enabled = false;
    this.observer = null;
    this.loadingIndicator = null;

    this.init();
  }

  init() {
    if (!this.container || !this.pagination) {
      console.warn('Infinite scroll: Required elements not found');
      return;
    }

    // Check if pagination exists (if only one page, no need for infinite scroll)
    const nextPageLink = this.getNextPageUrl();
    if (!nextPageLink) {
      console.log('Infinite scroll: No more pages to load');
      return;
    }

    this.enabled = true;
    this.createLoadingIndicator();
    this.setupIntersectionObserver();
    this.hidePagination();
  }

  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'infinite-scroll-loading';
    this.loadingIndicator.innerHTML = `
      <div class="loading-overlay__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
      <span class="infinite-scroll-loading__text">Loading more products...</span>
    `;

    // Insert after the product grid
    if (this.pagination) {
      this.pagination.parentNode.insertBefore(this.loadingIndicator, this.pagination);
    }
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '200px', // Start loading 200px before reaching the indicator
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loading) {
          this.loadNextPage();
        }
      });
    }, options);

    // Observe the loading indicator
    if (this.loadingIndicator) {
      this.observer.observe(this.loadingIndicator);
    }
  }

  hidePagination() {
    if (this.pagination) {
      this.pagination.style.display = 'none';
    }
  }

  getNextPageUrl() {
    if (!this.pagination) return null;

    const nextButton = this.pagination.querySelector('.pagination__item--next');
    if (!nextButton) return null;

    return nextButton.getAttribute('href');
  }

  async loadNextPage() {
    if (this.loading) return;

    const nextPageUrl = this.getNextPageUrl();
    if (!nextPageUrl) {
      this.onComplete();
      return;
    }

    this.loading = true;
    this.showLoading();

    try {
      const response = await fetch(nextPageUrl);
      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get the new products
      const newProducts = doc.querySelectorAll('#product-grid .collection-grid-item');

      if (newProducts.length === 0) {
        this.onComplete();
        return;
      }

      // Append new products to the grid
      newProducts.forEach(product => {
        this.container.appendChild(product.cloneNode(true));
      });

      // Update pagination
      const newPagination = doc.querySelector('.pagination-wrapper');
      if (newPagination) {
        const oldPagination = this.pagination;
        this.pagination = newPagination;
        oldPagination.parentNode.replaceChild(newPagination, oldPagination);
        this.hidePagination();
      } else {
        // No more pages
        this.onComplete();
        return;
      }

      // Re-initialize any components that need it
      this.reinitializeComponents();

      // Trigger scroll animations if they exist
      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger();
      }

      this.loading = false;
      this.hideLoading();

      // Check if we need to load more (if user has a very tall screen)
      setTimeout(() => {
        this.checkIfNeedMoreContent();
      }, 100);

    } catch (error) {
      console.error('Infinite scroll error:', error);
      this.loading = false;
      this.hideLoading();
      this.showError();
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.add('infinite-scroll-loading--active');
    }
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.remove('infinite-scroll-loading--active');
    }
  }

  showError() {
    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <p class="infinite-scroll-error">Failed to load more products. <a href="${this.getNextPageUrl()}">Try again</a></p>
      `;
    }
  }

  onComplete() {
    this.enabled = false;

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.innerHTML = `
        <p class="infinite-scroll-complete">You've reached the end of the collection</p>
      `;
    }
  }

  checkIfNeedMoreContent() {
    if (!this.loadingIndicator || !this.enabled) return;

    const rect = this.loadingIndicator.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight;

    if (isVisible && !this.loading) {
      this.loadNextPage();
    }
  }

  reinitializeComponents() {
    // Re-initialize quick add buttons if they exist
    if (typeof window.QuickAddBulk !== 'undefined') {
      document.querySelectorAll('quick-add-bulk').forEach(element => {
        if (!element.initialized) {
          element.initialized = true;
        }
      });
    }

    // Re-initialize any product cards that need event listeners
    const newCards = this.container.querySelectorAll('.collection-grid-item:not([data-initialized])');
    newCards.forEach(card => {
      card.setAttribute('data-initialized', 'true');

      // Trigger any custom card initialization if needed
      const event = new CustomEvent('product-card:added', {
        detail: { element: card }
      });
      document.dispatchEvent(event);
    });
  }

  // Public method to destroy the infinite scroll
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.loadingIndicator) {
      this.loadingIndicator.remove();
    }

    if (this.pagination) {
      this.pagination.style.display = '';
    }

    this.enabled = false;
  }

  // Public method to manually trigger load
  loadMore() {
    if (this.enabled && !this.loading) {
      this.loadNextPage();
    }
  }
}

// Initialize on DOM ready
function initInfiniteScroll() {
  // Check if infinite scroll is enabled via section settings
  const gridContainer = document.getElementById('ProductGridContainer');
  if (!gridContainer) return;

  // Look for a data attribute that might be set by the section
  const infiniteScrollEnabled = gridContainer.dataset.infiniteScroll === 'true';

  // Only initialize if the data attribute is explicitly set to true
  if (infiniteScrollEnabled) {
    window.infiniteScroll = new InfiniteScroll();
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  initInfiniteScroll();
}

// Reinitialize on section reload (Shopify theme editor)
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('#ProductGridContainer')) {
    // Destroy old instance if it exists
    if (window.infiniteScroll) {
      window.infiniteScroll.destroy();
    }

    // Create new instance
    setTimeout(initInfiniteScroll, 100);
  }
});

// Handle facet filtering - infinite scroll should reset
document.addEventListener('facet-filters-updated', () => {
  if (window.infiniteScroll) {
    window.infiniteScroll.destroy();
  }

  setTimeout(initInfiniteScroll, 500);
});
